import { memo, useEffect, useRef } from 'react'
import { Stack, Text } from 'ui/baseComponents'
import ReportSection from 'ui/clinicianScreens/Patient/Report/components/ReportSection'
import EditableContent from 'ui/clinicianScreens/Patient/Report/components/EditableContent'
import { SECTION_TITLES } from 'ui/clinicianScreens/Patient/Report/consts'
import useClinicalVisits from 'ui/hooks/useClinicalVisits'
import useBoundStore from 'domains/zustand/store'

const ReportSectionDyskinesiaNextSteps = ({
  hiddenSections,
  hasDataSet,
  addToHasDataSet,
  removeFromHasDataSet,
  setHiddenSections,
  isClinicianReport,
  ...props
}) => {
  const { dyskinesiaManagementRecommendation, loading, hasError } =
    useClinicalVisits()

  const stateKeyPrefix = isClinicianReport ? 'Clinician' : 'Patient'
  const highlightsEdits = useBoundStore((state) => state.highlightsEdits)
  const setHighlightsEdits = useBoundStore((state) => state.setHighlightsEdits)

  const initialStateSetRef = useRef(false)

  useEffect(() => {
    if (!loading && !hasError && !initialStateSetRef.current) {
      initialStateSetRef.current = true

      if (
        dyskinesiaManagementRecommendation &&
        dyskinesiaManagementRecommendation !== null
      ) {
        // Has data: checkbox checked, section visible
        addToHasDataSet(SECTION_TITLES.DYSKINESIA_MANAGEMENT)
        setHiddenSections((prev) => {
          const next = new Set(prev)
          next.delete(SECTION_TITLES.DYSKINESIA_MANAGEMENT)
          return next
        })
      } else {
        // No data: checkbox unchecked, section hidden
        removeFromHasDataSet(SECTION_TITLES.DYSKINESIA_MANAGEMENT)
        setHiddenSections(
          (prev) => new Set(prev.add(SECTION_TITLES.DYSKINESIA_MANAGEMENT)),
        )
      }
    }
  }, [
    loading,
    hasError,
    dyskinesiaManagementRecommendation,
    addToHasDataSet,
    removeFromHasDataSet,
    setHiddenSections,
  ])

  const getDyskinesiaRecommendations = () => {
    if (loading) {
      return <Text variant="body16">Loading dyskinesia recommendations...</Text>
    }

    if (hasError) {
      return (
        <Text variant="body16">Unable to load dyskinesia recommendations.</Text>
      )
    }

    if (
      !dyskinesiaManagementRecommendation ||
      dyskinesiaManagementRecommendation === null
    ) {
      return (
        <Text variant="body16">
          <EditableContent
            stateKey={`${stateKeyPrefix} Dyskinesia Management Default Text`}
            stateSrc={highlightsEdits}
            stateSaveCallback={setHighlightsEdits}
            dataCy="editable-content-dyskinesia-default"
          >
            No dyskinesia management recommendations available at this time.
          </EditableContent>
        </Text>
      )
    }

    // Force correct order by defining key sequence explicitly
    const keyOrder = [
      'Summary',
      'Best practice next step',
      'Specific option',
      'Sequencing',
      'Coverage note',
    ]

    // Get all keys that exist in the object (handles dynamic keys not in our predefined list)
    const existingKeys = Object.keys(dyskinesiaManagementRecommendation || {})
    const unknownKeys = existingKeys.filter((key) => !keyOrder.includes(key))
    const orderedKeys = [
      ...keyOrder.filter((key) => existingKeys.includes(key)),
      ...unknownKeys,
    ]

    return (
      <Stack spacing={2}>
        {orderedKeys.map((key) => {
          const value = dyskinesiaManagementRecommendation[key]
          if (!value) return null

          const stateKey = `${stateKeyPrefix} Dyskinesia Management ${key}`
          const dataCy = `editable-content-dyskinesia-${key
            .toLowerCase()
            .replace(/\s+/g, '-')}`

          // All keys: display key as bold header followed by editable content
          return (
            <Text key={key} variant="body16" sx={{ textAlign: 'left' }}>
              <Text
                component="span"
                variant="body16B"
                color="text.primary"
                sx={{ display: 'inline' }}
              >
                {key}:{' '}
              </Text>
              <EditableContent
                stateKey={stateKey}
                stateSrc={highlightsEdits}
                stateSaveCallback={setHighlightsEdits}
                dataCy={dataCy}
              >
                {value}
              </EditableContent>
            </Text>
          )
        })}
      </Stack>
    )
  }

  return (
    <ReportSection
      title={SECTION_TITLES.DYSKINESIA_MANAGEMENT}
      hidden={hiddenSections.has(SECTION_TITLES.DYSKINESIA_MANAGEMENT)}
      {...props}
    >
      {getDyskinesiaRecommendations()}
    </ReportSection>
  )
}

export default memo(ReportSectionDyskinesiaNextSteps)
